import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import ChatMessageBubble from './ChatMessageBubble';
import { createChatSocket, emitAck } from '../../lib/chatSocket';
import { ACCEPT, checkFile, summarise } from '../../lib/chatFiles';
import useScrollBackLoader from '../../components/chat/useScrollBackLoader';
import { Button, Skeleton, EmptyState, useToast } from '../../design-system';
import '../../org-admin/styles/Chat.css';

// Staff-to-staff chat, self-contained so it can be dropped into either shell:
// the cockpit's Chat page as a tab, and the employee area as its own page.
//
// It owns its own socket rather than sharing one with the client-chat page,
// because only one of the two is mounted at a time and a socket per mounted
// chat surface is simpler than threading one through a context for no gain.

const fmtDay = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(date, today)) return 'Today';
  if (same(date, yest)) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtRel = (d) => {
  if (!d) return '';
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
};

function TeamChat({ onUnreadChange }) {
  const { user } = useAuth();
  const toast = useToast();
  const myId = String(user?.userId || '');

  const [threads, setThreads] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState('');

  const [activeId, setActiveId] = useState('');
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [connected, setConnected] = useState(false);
  const [peerTyping, setPeerTyping] = useState(null);

  const socketRef = useRef(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  // Older messages load as the reader scrolls up; the hook preserves their
  // reading position across the prepend.
  const { scrollRef, onScroll } = useScrollBackLoader({ hasMore, loadOlder: () => loadOlder() });
  const typingTimer = useRef(null);
  // The open conversation, readable from socket handlers without re-binding
  // them on every change.
  const activeRef = useRef({ userId: '', threadId: '' });

  // ── Socket ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = createChatSocket('staff');
    socketRef.current = socket;
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('dm:message', (msg) => {
      if (String(msg.threadId) !== String(activeRef.current.threadId)) return;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    });

    socket.on('dm:thread', (row) => {
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.userId === row.userId);
        const next = idx === -1 ? [row, ...prev] : prev.map((t, i) => (i === idx ? { ...t, ...row } : t));
        return [...next].sort((a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0));
      });
      // Once someone has a conversation they leave the "start a chat" list.
      setColleagues((prev) => prev.filter((c) => c.userId !== row.userId));
    });

    socket.on('dm:typing', (evt) => {
      if (String(evt.threadId) !== String(activeRef.current.threadId)) return;
      setPeerTyping(evt.isTyping ? evt.who : null);
      if (evt.isTyping) {
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setPeerTyping(null), 4000);
      }
    });

    return () => {
      clearTimeout(typingTimer.current);
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, []);

  // ── Inbox ─────────────────────────────────────────────────────────────────
  const loadList = useCallback(() => {
    setLoadingList(true);
    apiClient
      .get('/api/dm/threads')
      .then((r) => {
        setThreads(r.data.threads || []);
        setColleagues(r.data.colleagues || []);
        setEnabled(r.data.enabled !== false);
      })
      .catch((e) => toast.danger(e.response?.data?.error || 'Failed to load conversations'))
      .finally(() => setLoadingList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(loadList, [loadList]);

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);
  useEffect(() => { onUnreadChange?.(totalUnread); }, [totalUnread, onUnreadChange]);

  // ── One conversation ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeId) { setThread(null); setMessages([]); activeRef.current = { userId: '', threadId: '' }; return undefined; }
    setLoadingThread(true);
    setReplyTo(null);
    setPeerTyping(null);
    let openedThreadId = '';

    apiClient
      .get(`/api/dm/threads/${activeId}`)
      .then((r) => {
        setThread(r.data.thread);
        setMessages(r.data.messages || []);
        setHasMore(!!r.data.hasMore);
        setEnabled(r.data.enabled !== false);
        openedThreadId = r.data.thread?.id || '';
        activeRef.current = { userId: activeId, threadId: openedThreadId };
        setThreads((prev) => prev.map((t) => (t.userId === activeId ? { ...t, unread: 0 } : t)));
        const socket = socketRef.current;
        if (socket && openedThreadId) emitAck(socket, 'dm:open', { threadId: openedThreadId });
      })
      .catch((e) => toast.danger(e.response?.data?.error || 'Failed to open conversation'))
      .finally(() => setLoadingThread(false));

    return () => {
      const socket = socketRef.current;
      if (socket && openedThreadId) socket.emit('dm:close', { threadId: openedThreadId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Follow the conversation down only when something was APPENDED (a new
  // message arrived, or the thread changed) — never on a PREPEND from scrolling
  // up, which would yank the reader straight back to the bottom and make
  // loading history impossible.
  const lastIdRef = useRef(null);
  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id || null;
    if (lastId !== lastIdRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
      lastIdRef.current = lastId;
    }
  }, [messages, loadingThread]);

  const loadOlder = async () => {
    const oldest = messages[0];
    if (!oldest) return;
    try {
      const r = await apiClient.get(`/api/dm/threads/${activeId}/messages`, { params: { before: oldest.sentAt } });
      setMessages((prev) => [...(r.data.messages || []), ...prev]);
      setHasMore(!!r.data.hasMore);
    } catch {
      toast.danger('Could not load older messages');
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const replyToId = replyTo?.id || null;
      const socket = socketRef.current;
      // Socket first, REST as the fallback — identical rows either way.
      const res = socket?.connected
        ? await emitAck(socket, 'dm:send', { userId: activeId, body, replyToId })
        : { error: 'offline' };
      if (res.ok) {
        setDraft('');
        setReplyTo(null);
      } else {
        const r = await apiClient.post(`/api/dm/threads/${activeId}/messages`, { body, replyToId });
        setDraft('');
        setReplyTo(null);
        setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
      }
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Message not sent');
    } finally {
      setSending(false);
    }
  };

  const sendFile = async (file) => {
    const problem = checkFile(file);
    if (problem) { toast.danger(problem); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (draft.trim()) form.append('body', draft.trim());
      if (replyTo?.id) form.append('replyToId', replyTo.id);
      const r = await apiClient.post(`/api/dm/threads/${activeId}/attachments`, form);
      setDraft('');
      setReplyTo(null);
      setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
    } catch (err) {
      toast.danger(err.response?.data?.error || 'File could not be sent');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openAttachment = async (file) => {
    try {
      const r = await apiClient.get(`/api/dm/attachments/${file.documentId}/url`);
      return r.data.source === 'stream' ? `${apiClient.defaults.baseURL || ''}${r.data.url}` : r.data.url;
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not open that file');
      return null;
    }
  };

  const jumpToReply = (id) => {
    const el = document.getElementById(`dm-msg-${id}`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('chat-msg-flash');
      setTimeout(() => el.classList.remove('chat-msg-flash'), 1200);
    }
  };

  const onDraft = (v) => {
    setDraft(v);
    const socket = socketRef.current;
    const threadId = activeRef.current.threadId;
    if (socket?.connected && threadId) socket.emit('dm:typing', { threadId, isTyping: v.length > 0 });
  };

  // Conversations first, then everyone else you could start one with.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (r) => !q || (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
    return {
      threads: threads.filter(match),
      colleagues: colleagues.filter(match),
    };
  }, [threads, colleagues, search]);

  const rows = [];
  let lastDay = null;
  messages.forEach((m) => {
    const day = fmtDay(m.sentAt);
    if (day !== lastDay) { rows.push({ kind: 'day', id: `d-${day}-${m.id}`, day }); lastDay = day; }
    rows.push({ kind: 'msg', ...m });
  });

  return (
    <div className="chat-wrap">
      <aside className="chat-list">
        <div className="chat-list-head">
          <input
            className="chat-search"
            placeholder="🔎 Search colleagues…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className={`chat-conn chat-conn-${connected ? 'on' : 'off'}`}>
            <span className="chat-conn-dot" />
            {connected ? 'Live' : 'Reconnecting…'}
          </span>
        </div>

        <div className="chat-list-scroll">
          {loadingList ? (
            <div className="chat-list-loading"><Skeleton.Text lines={6} /></div>
          ) : (
            <>
              {visible.threads.map((t) => (
                <button
                  type="button"
                  key={t.userId}
                  className={`chat-row${t.userId === activeId ? ' is-active' : ''}`}
                  onClick={() => setActiveId(t.userId)}
                >
                  <span className="chat-avatar" aria-hidden>{(t.name || '?').charAt(0).toUpperCase()}</span>
                  <span className="chat-row-main">
                    <span className="chat-row-top">
                      <span className="chat-row-name">{t.name}</span>
                      <span className="chat-row-time">{fmtRel(t.lastMessageAt)}</span>
                    </span>
                    <span className="chat-row-bottom">
                      <span className="chat-row-preview">
                        {t.lastMessageMine && <span className="chat-row-you">You: </span>}
                        {t.lastMessagePreview || <em>No messages yet</em>}
                      </span>
                      {t.unread > 0 && <span className="chat-badge">{t.unread}</span>}
                    </span>
                  </span>
                </button>
              ))}

              {visible.colleagues.length > 0 && (
                <p className="chat-list-heading">Start a conversation</p>
              )}
              {visible.colleagues.map((c) => (
                <button
                  type="button"
                  key={c.userId}
                  className={`chat-row${c.userId === activeId ? ' is-active' : ''}`}
                  onClick={() => setActiveId(c.userId)}
                >
                  <span className="chat-avatar chat-avatar-muted" aria-hidden>{(c.name || '?').charAt(0).toUpperCase()}</span>
                  <span className="chat-row-main">
                    <span className="chat-row-top"><span className="chat-row-name">{c.name}</span></span>
                    <span className="chat-row-bottom"><span className="chat-row-preview">{c.email}</span></span>
                  </span>
                </button>
              ))}

              {visible.threads.length === 0 && visible.colleagues.length === 0 && (
                <p className="chat-empty-note">No colleagues match.</p>
              )}
            </>
          )}
        </div>
      </aside>

      <section className="chat-pane">
        {!activeId ? (
          <EmptyState icon="💬" title="Pick a colleague" description="Choose someone on the left to start or continue a conversation." />
        ) : (
          <>
            <header className="chat-pane-head">
              <div>
                <h3 className="chat-pane-title">{thread?.name || '…'}</h3>
                <p className="chat-pane-sub">{thread?.email || 'Private conversation'}</p>
              </div>
            </header>

            <div className="chat-scroll" ref={scrollRef} onScroll={onScroll}>
              {loadingThread ? (
                <div className="chat-loading"><Skeleton.Text lines={8} /></div>
              ) : (
                <>
                  {hasMore && (
                    <div className="chat-older">
                      <Button variant="secondary" size="sm" onClick={loadOlder}>Load older messages</Button>
                    </div>
                  )}
                  {rows.length === 0 && (
                    <p className="chat-empty-note chat-empty-center">
                      No messages yet — say hello.
                    </p>
                  )}
                  {rows.map((r) =>
                    r.kind === 'day' ? (
                      <div className="chat-day" key={r.id}><span>{r.day}</span></div>
                    ) : (
                      <div key={r.id} id={`dm-msg-${r.id}`}>
                        <ChatMessageBubble
                          message={r}
                          mine={String(r.senderId) === myId}
                          // A 1:1 conversation never needs a name over a bubble:
                          // side already says who is speaking.
                          showSender={false}
                          onReply={setReplyTo}
                          onOpenAttachment={openAttachment}
                          onJumpToReply={jumpToReply}
                        />
                      </div>
                    )
                  )}
                  {peerTyping && <div className="chat-typing">{peerTyping} is typing…</div>}
                  <div ref={bottomRef} />
                </>
              )}
            </div>

            {!enabled && (
              <p className="chat-error">Staff messaging has been switched off by an administrator.</p>
            )}

            {replyTo && (
              <div className="chat-reply-strip">
                <div className="chat-reply-strip-main">
                  <span className="chat-reply-strip-who">
                    Replying to {String(replyTo.senderId) === myId ? 'yourself' : replyTo.senderName}
                  </span>
                  <span className="chat-reply-strip-text">{summarise(replyTo)}</span>
                </div>
                <button type="button" className="chat-reply-cancel" onClick={() => setReplyTo(null)} aria-label="Cancel reply">✕</button>
              </div>
            )}

            <form className="chat-composer" onSubmit={send}>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="chat-file-input"
                onChange={(e) => e.target.files?.[0] && sendFile(e.target.files[0])}
              />
              <button
                type="button"
                className="chat-attach-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || !enabled}
                title="Attach a file"
                aria-label="Attach a file"
              >
                {uploading ? '…' : '📎'}
              </button>
              <textarea
                className="chat-input"
                placeholder={enabled ? 'Write a message…' : 'Staff messaging is switched off'}
                value={draft}
                rows={1}
                disabled={!enabled}
                onChange={(e) => onDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
              />
              <Button type="submit" variant="primary" disabled={!draft.trim() || sending || !enabled} loading={sending}>
                Send
              </Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

export default TeamChat;
